import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from "../assets/images/logo.png";
import { searchProducts } from './Products/productService';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);
  const navLinks = [
    { label: 'BALLINA', to: '/' },
    { label: 'DYQANI', to: '/products' },
    { label: 'VESHJE SPORTI', to: '/products' },
    { label: 'AKSESORË', to: '/products' },
    { label: 'OFERTA & ZBRITJE', to: '/products' },
    { label: 'BRENDET', to: '/products' },
  ];

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = useCallback(() => setIsOpen(false), []);
  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    setIsOpen(false);
  }, []);
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setIsSearching(false);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
  }, [searchAbortRef, searchDebounceRef]);

  const handleSearchInput = (event) => setSearchQuery(event.target.value);
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchQuery((prev) => prev.trim());
  };

  const handleResultClick = useCallback(() => {
    closeSearch();
  }, [closeSearch]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.body.style.overflow = isOpen || isSearchOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isSearchOpen]);

  useEffect(() => () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
  }, []);

  useEffect(() => {
    if (!isSearchOpen) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      setIsSearching(false);
      setSearchResults([]);
      setSearchError(null);
      return undefined;
    }

    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      setIsSearching(false);
      setSearchResults([]);
      setSearchError(null);
      return undefined;
    }

    setIsSearching(true);
    const controller = new AbortController();
    searchAbortRef.current = controller;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchProducts(trimmedQuery, { signal: controller.signal });
        setSearchResults(results.slice(0, 12));
        setSearchError(null);
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
        setSearchError('Kërkimi dështoi. Provoni përsëri.');
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 400);

    searchDebounceRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [isSearchOpen, searchQuery]);

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, closeSearch]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="flex items-center justify-between px-4 md:px-8 py-4">
          <Link to="/" className="flex-shrink-0" onClick={closeSearch}>
            <img src={Logo} alt="Logo" className="w-[217px]" />
          </Link>
          <div className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-sm text-white transition hover:text-gray-200"
                onClick={closeSearch}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center space-x-4">
            <button
              className="text-white md:hidden"
              onClick={toggleMenu}
              aria-label={isOpen ? 'Mbyll menunë' : 'Hap menunë'}
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            <div className="hidden md:flex space-x-4">
              <SearchIcon onClick={openSearch} ariaPressed={isSearchOpen} />
              <Link to="/wishlist" onClick={closeSearch}><HeartIcon /></Link>
              <Link to="/cart" onClick={closeSearch}><CartIcon /></Link>
              <Link to="/login" onClick={closeSearch}><UserIcon /></Link>
            </div>
          </div>
        </div>
      </nav>

      {isOpen && (
        <div
          className="md:hidden fixed inset-0 z-[70] bg-black/95 text-white"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-6 pt-6 border-b border-white/10 pb-4">
              <Link to="/" onClick={closeMenu} className="flex-shrink-0">
                <img src={Logo} alt="Logo" className="w-36" />
              </Link>
              <button
                type="button"
                className="p-3 rounded-full border border-white/20"
                onClick={closeMenu}
                aria-label="Mbyll menunë"
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 mt-10 space-y-5">
              {navLinks.map((link) => (
                <Link
                  key={`mobile-${link.label}`}
                  to={link.to}
                  className="block text-2xl font-semibold tracking-wide"
                  onClick={() => {
                    closeMenu();
                    closeSearch();
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="px-6 pb-10">
              <div className="grid grid-cols-4 gap-4 text-center">
                <SearchIcon
                  className="border border-white/20 rounded-xl py-3 flex items-center justify-center"
                  onClick={() => {
                    closeMenu();
                    openSearch();
                  }}
                />
                <Link
                  to="/wishlist"
                  onClick={() => {
                    closeMenu();
                    closeSearch();
                  }}
                  className="py-3 border border-white/20 rounded-xl flex items-center justify-center"
                >
                  <HeartIcon />
                </Link>
                <Link
                  to="/cart"
                  onClick={() => {
                    closeMenu();
                    closeSearch();
                  }}
                  className="py-3 border border-white/20 rounded-xl flex items-center justify-center"
                >
                  <CartIcon />
                </Link>
                <Link
                  to="/login"
                  onClick={() => {
                    closeMenu();
                    closeSearch();
                  }}
                  className="py-3 border border-white/20 rounded-xl flex items-center justify-center"
                >
                  <UserIcon />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSearchOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm px-3 py-4 sm:px-6"
          role="dialog"
          aria-modal="true"
          onClick={closeSearch}
        >
          <div
            className="mx-auto flex h-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-black/75 text-white shadow-2xl lg:mt-24 lg:h-auto lg:max-h-[85vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-col gap-4 border-b border-white/10 px-4 py-5 sm:px-6 lg:border-b-0 lg:px-8 lg:py-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <form onSubmit={handleSearchSubmit} className="flex-1 w-full">
                  <div className="flex items-center gap-3 rounded-[8px] border border-white/25 bg-white/5 px-4 py-3 focus-within:border-white lg:bg-transparent lg:px-5 lg:py-4">
                    <span className="hidden sm:block lg:hidden">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18C12.8482 18 14.5319 17.2976 15.799 16.1115L19.2929 19.6054" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      autoFocus
                      value={searchQuery}
                      onChange={handleSearchInput}
                      placeholder="Kërko për produkte, kategori ose brende"
                      className="w-full bg-transparent text-base outline-none placeholder:text-white/50 lg:text-lg"
                    />
                  </div>
                </form>
                <button
                  type="button"
                  className="w-full  px-4 py-3 text-center text-sm font-semibold uppercase tracking-wide transition hover:border-white sm:w-auto lg:text-base"
                  onClick={closeSearch}
                >
                  <SearchIcon />
                </button>
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/60 lg:hidden">Rezultatet e kërkimit</p>
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-2 sm:px-4 lg:px-6">
              {isSearching && (
                <p className="py-10 text-center text-sm text-gray-300">Duke kërkuar produkte...</p>
              )}
              {!isSearching && searchError && (
                <p className="py-10 text-center text-sm text-red-300">{searchError}</p>
              )}
              {!isSearching && !searchError && searchResults.length === 0 && searchQuery.trim() && (
                <p className="py-10 text-center text-sm text-gray-400">Asnjë produkt nuk u gjet për "{searchQuery.trim()}".</p>
              )}
              {!isSearching && !searchError && searchResults.length === 0 && !searchQuery.trim() && (
                <p className="py-10 text-center text-sm text-gray-400">Fillo të shkruash për të kërkuar produkte.</p>
              )}

              <ul className="space-y-2 lg:space-y-0 lg:divide-y lg:divide-white/5">
                {searchResults.map((product, index) => {
                  const imageSrc = product.image || product.imageUrls?.[0];
                  const productId = product.id || product.productCode;
                  const productLink = productId ? `/products/${encodeURIComponent(productId)}` : '/products';
                  const price = typeof product.price === 'number' ? product.price : Number(product.price) || 0;
                  const currency = product.currency || '€';

                  return (
                    <li key={productId || product.slug || `search-${index}`}>
                      <Link
                        to={productLink}
                        onClick={handleResultClick}
                        className="flex items-center gap-4 rounded-2xl border border-white/10 px-3 py-3 transition hover:bg-white/5 lg:rounded-none lg:border-none lg:px-2 lg:py-4"
                        state={{ product, fromSearch: true }}
                      >
                        {imageSrc && (
                          <img
                            src={imageSrc}
                            alt={product.name || product.title}
                            className="h-16 w-16 flex-shrink-0 rounded-2xl object-cover sm:h-20 sm:w-20"
                            loading="lazy"
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-[11px] uppercase tracking-[0.3em] text-gray-400">{product.brand || 'Brand i panjohur'}</p>
                          <p className="text-base font-semibold sm:text-lg">{product.name || product.title}</p>
                          <p className="text-sm text-gray-300">
                            {currency}
                            {price.toFixed(2)}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
const SearchIcon = ({ className = '', onClick, ariaPressed }) => (
  <button
    type="button"
    className={`text-white transition ${className}`}
    onClick={onClick}
    aria-label="Kërko"
    aria-pressed={ariaPressed}
  >
    <svg width="30" height="30" viewBox="0 0 27 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.7782 2.88965C18.6115 2.88965 23.3338 7.61187 23.3338 13.4452C23.3338 19.2785 18.6115 24.0008 12.7782 24.0008C6.94488 24.0008 2.22266 19.2785 2.22266 13.4452C2.22266 9.33409 4.5671 5.77854 8.00043 4.03409" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M24.4449 25.1119L22.2227 22.8896" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const HeartIcon = () => (
  <svg width="30" height="30" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="23" cy="23" r="22.5" stroke="#D9D9D9" />
    <path d="M18 16C15.239 16 13 18.216 13 20.95C13 23.157 13.875 28.395 22.488 33.69C22.6423 33.7839 22.8194 33.8335 23 33.8335C23.1806 33.8335 23.3577 33.7839 23.512 33.69C32.125 28.395 33 23.157 33 20.95C33 18.216 30.761 16 28 16C25.239 16 23 19 23 19C23 19 20.761 16 18 16Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
  </svg>

);

const CartIcon = () => (
  <svg width="30" height="30" viewBox="0 0 47 46" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="23.9766" cy="23" r="22.5" stroke="#D9D9D9" />
    <path d="M23.0391 11.0938C22.4766 11.2188 21.9922 11.4062 21.5859 11.6562C21.1797 11.9062 20.7734 12.25 20.3672 12.6875C19.9609 13.125 19.6328 13.5938 19.3828 14.0938C19.1328 14.5938 19.0078 15.0938 19.0078 15.5938V15.9688H14.0391L13.9453 35H34.0078V15.9688H28.9453V15.5938C28.9453 15.0938 28.8203 14.5938 28.5703 14.0938C28.3203 13.5938 27.9766 13.0938 27.5391 12.5938C27.1016 12.0938 26.6172 11.7188 26.0859 11.4688C25.5547 11.2188 24.9141 11.0625 24.1641 11C23.6641 11 23.2578 11.0312 22.9453 11.0938H23.0391ZM25.1953 13.25C25.6328 13.4375 26.0078 13.75 26.3203 14.1875C26.6328 14.625 26.8516 15.0938 26.9766 15.5938V15.9688H20.9766V15.5938C21.1016 14.9688 21.3828 14.4219 21.8203 13.9531C22.2578 13.4844 22.7578 13.1875 23.3203 13.0625C23.6328 13 23.9609 12.9844 24.3047 13.0156C24.6484 13.0469 24.9453 13.125 25.1953 13.25ZM19.0078 19.5312V21.0312H20.9766V18.0312H26.9766V21.0312H28.9453V18.0312H31.9453V33.0312H16.0078V18.0312H19.0078V19.5312Z" fill="white" />
  </svg>

);

const UserIcon = () => (
  <svg width="30" height="30" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 34.0004V31.2125C32 29.7337 31.4943 28.3155 30.5941 27.2698C29.6939 26.2242 28.473 25.6367 27.2 25.6367H18.8C17.527 25.6367 16.3061 26.2242 15.4059 27.2698C14.5057 28.3155 14 29.7337 14 31.2125V34.0004" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M22.9991 20.4091C25.6306 20.4091 27.7638 18.3028 27.7638 15.7045C27.7638 13.1063 25.6306 11 22.9991 11C20.3676 11 18.2344 13.1063 18.2344 15.7045C18.2344 18.3028 20.3676 20.4091 22.9991 20.4091Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    <circle cx="23" cy="23" r="22.5" stroke="#D9D9D9" />
  </svg>

);

export default Navbar;
